import React, { useMemo } from 'react';
import classes from './PostCardMediaPreviewTwitter.module.scss';
import { SocialProfileWithBoards } from '../../../../hooks/useCategoriesProfilesWithBoards';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import {
  LinkTwitterCard,
  MediaType,
  Network,
  PostMultiMediaAttachMode,
  SocialOAuthProvider
} from '../../../../models/graphql';
import { serializeText, serializeTextToHtml } from '../../TextEditor/helpers/serializer';
import classNames from 'classnames';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import { Loader } from '../../../../components/containers/Loader';
import { TwitterCard } from '../TwitterCard';
import { InvalidMediaSet } from '../PostCardMediaPreview/InvalidMediaSet';
import { SingularValidator } from '../../validators/helpers/singular-validator';
import { MediaTypePostEditorState } from '../../../../models/post-editor';

export type PostCardMediaPreviewTwitterProfile = {
  socialProfile: SocialProfileWithBoards & { __typename: 'TwitterSocialProfile' };
};

export type PostCardMediaPreviewTwitterViewProps = PostCardPreviewMediaDataType & PostCardMediaPreviewTwitterProfile;

export const isInvalidTwitterMedia = (media: MediaTypePostEditorState[]): boolean =>
  SingularValidator.noMixedMediaInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
  SingularValidator.twitterMediaLimitInvalid(media, PostMultiMediaAttachMode.MULTIPLE);

export const PostCardMediaPreviewTwitterView = (props: PostCardMediaPreviewTwitterViewProps): JSX.Element => {
  const { text, media, socialProfile, link } = props;
  const loadingLinkMetadata = link && link.linkMetadata === undefined;
  const linkMetadata = link?.linkMetadata;
  const hasText = !!serializeText(text);
  const hasInavlidMedia = isInvalidTwitterMedia(media);

  const getMedia = useMemo(() => {
    if (!media.length) return <></>;
    const baseMediaClasses = media.length > 1 ? classes.MediaItem : classes.MediaItemSolo;
    let computedMediaItemClasses = '';
    const getClasses = (index: number, mediaLength: number): string => {
      switch (mediaLength) {
        case 2:
          computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaTallHalf, {
            [classes.TopLeft]: index === 0,
            [classes.TopRight]: index === 1
          });
          break;
        case 3:
          computedMediaItemClasses = classNames(
            baseMediaClasses,
            index === 0 ? classes.MediaTallHalf : classes.MediaWideFourth,
            {
              [classes.TopLeft]: index === 0,
              [classes.TopRight]: index === 1,
              [classes.BottomRight]: index === 2
            }
          );
          break;
        case 4:
          computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaWideFourth, {
            [classes.TopLeft]: index === 0,
            [classes.TopRight]: index === 1,
            [classes.BottomLeft]: index === 2,
            [classes.BottomRight]: index === 3
          });
          break;
        case 1:
        default:
          computedMediaItemClasses = classNames(baseMediaClasses);
          break;
      }
      return computedMediaItemClasses;
    };
    return (
      <>
        <div
          className={classNames(classes.MediaWrapper, hasText ? classes.MediaWrapperHasText : null, {
            [classes.MediaWrapperMultipleImages]: media.length > 1 && !hasInavlidMedia
          })}>
          {hasInavlidMedia ? (
            <InvalidMediaSet className={classes.NoContent} />
          ) : (
            media
              .filter((_, index) => index < 4)
              .map((mediaItem, index) => {
                return (
                  <div className={getClasses(index, media.length)} key={`media-item-${index}`}>
                    <img
                      src={mediaItem.type === MediaType.VIDEO ? mediaItem.thumbnailUrl : mediaItem.url}
                      alt={`X (Twitter) preview ${index + 1}`}
                    />
                    {media[index].type === MediaType.VIDEO ? (
                      <div className={classes.MediaMarker}>
                        <Icon name={IconsEnum.play} size={'large'} />
                      </div>
                    ) : null}
                    {media[index].gif ? (
                      <div className={classes.GifMarker}>
                        <Icon name={IconsEnum.gifBadge} size={'large'} />
                      </div>
                    ) : null}
                  </div>
                );
              })
          )}
        </div>
      </>
    );
  }, [media, hasText, hasInavlidMedia]);

  return (
    <>
      <div className={classes.SocialProfileWrapper}>
        <SocialProfile
          socialProfile={{
            id: socialProfile.id,
            provider: SocialOAuthProvider.TWITTER,
            network: Network.TWITTER,
            name: socialProfile.displayName,
            type: ''
          }}
          disconnected={!socialProfile.connected}
          sublabel={true}
          size={'medium'}
          label={true}
          image={socialProfile.photoUrl}
        />
      </div>
      {hasText ? (
        <div
          className={classes.TextWrapper}
          dangerouslySetInnerHTML={{ __html: serializeTextToHtml(text, SocialNetworksEnum.twitter) }}></div>
      ) : null}
      {(loadingLinkMetadata || linkMetadata) && !media.length && link ? (
        <div className={classNames(classes.LinkPreviewWrapper, { [classes.LinkPreviewWrapperHasText]: hasText })}>
          {loadingLinkMetadata ? <Loader variation={'dark'} size={'small'} type={'inline'} /> : null}
          {linkMetadata && linkMetadata.twitterCard && linkMetadata.twitterCard.card ? (
            <>
              <TwitterCard
                link={link.url}
                card={linkMetadata.twitterCard.card || LinkTwitterCard.SUMMARY}
                description={linkMetadata.twitterCard?.description || linkMetadata.openGraph?.description || ''}
                title={linkMetadata.twitterCard?.title || linkMetadata.openGraph?.title || ''}
                image={linkMetadata.twitterCard?.image || linkMetadata.openGraph?.image || ''}
                __typename={'TwitterCard'}
              />
            </>
          ) : !loadingLinkMetadata ? (
            <BoxNotification type={'warning'}>
              <p>X (Twitter) Card not available</p>
              <p>
                We were not able to preview a X (Twitter) Card for this link. You may wish to{' '}
                <a href={'https://cards-dev.twitter.com/validator'} target={'_blank'} rel={'noreferrer'}>
                  double-check on X (Twitter)
                </a>
                .
              </p>
              <p>We recommend adding an image to ensure the tweet looks as engaging as possible.</p>
            </BoxNotification>
          ) : null}
        </div>
      ) : null}
      {media.length ? getMedia : null}
      <div className={classNames(classes.FooterWrapper, 'grid-x', 'align-justify')}>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.comments} size={'medium'} />
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.retweets} size={'medium'} />
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.likes} size={'medium'} />
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.moveUp} size={'medium'} />
          </div>
        </div>
      </div>
    </>
  );
};
